import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react'
import { APP_TRANSLATIONS, resolveLocale, type AppLocale, type AppTranslations } from './app'
import { tagFor, useLoadedLanguageRegistry } from './registry'
import { detectLocale } from './landing'
import { useLoadedLocale } from './useLoadedLocale'
import { useAuthStore } from '../stores/authStore'

interface LocaleContextValue {
  locale: AppLocale
  /** Ephemeral override — for onboarding screen language switch before user is persisted */
  setLocale: (locale: AppLocale) => void
  tr: AppTranslations
  /** The locale's regional tag (ADR-111 D1), for number formatting. */
  tag: string
  /**
   * A page that renders in a locale of its own (the landing page) claims the
   * document language while mounted; null releases it. One writer of
   * <html lang> means the provider and the page never overwrite each other.
   */
  claimDocumentLocale: (locale: AppLocale | null) => void
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user)
  const registry = useLoadedLanguageRegistry()
  // null = no ephemeral override; falls through to user.interface_language
  const [override, setOverride] = useState<AppLocale | null>(null)
  const [documentClaim, setDocumentClaim] = useState<AppLocale | null>(null)

  const setLocale = useCallback((locale: AppLocale) => setOverride(locale), [])
  const claimDocumentLocale = useCallback((locale: AppLocale | null) => setDocumentClaim(locale), [])

  // Signed in: the account's interface language wins. Signed out: the
  // visitor's saved choice, else the first browser language the app offers,
  // else English (the same choice the landing page makes).
  const wanted: AppLocale = override ?? (user ? resolveLocale(user.interface_language) : detectLocale(registry))

  // Moves to `wanted` only once its table is loaded (main.tsx loads the first
  // one before the first paint).
  const locale = useLoadedLocale(wanted)
  const tr = APP_TRANSLATIONS[locale] ?? APP_TRANSLATIONS.en
  const tag = tagFor(locale, registry)
  const documentTag = tagFor(documentClaim ?? locale, registry)

  useEffect(() => {
    document.documentElement.lang = documentTag
  }, [documentTag])

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, tr, tag, claimDocumentLocale }),
    [locale, setLocale, tr, tag, claimDocumentLocale],
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components -- the hook stays beside its context; 87 files import it from here
export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext)
  if (!ctx) throw new Error('useLocale must be used inside LocaleProvider')
  return ctx
}
